import React, { useState } from 'react'
import { Checkbox, PersonMultiPicker } from '../../../../components'
import { eventGuestService } from '../../services'

// Unified "add guests" dialog (rendered inside a modal via useEventDialog's
// showAddGuestsDialog): a single allowCreate multi-picker covers both
// picking any number of existing network persons and adding someone who
// isn't in the network yet (creates them in network.person through the
// same shared PersonEditForm flow used everywhere else in the app).
// Selections land in the `pending` list, rendered as the same grid as the
// bulk-edit dialog (`.ev-bulk-grid`): one row per person, one column per
// custom field, each cell individually editable, with a per-column "Fyll"
// control to stamp the whole column at once. Same dialogRef/hideModalDialog/
// setLoadingModalDialog/onSaved contract as EventEditForm/GuestEditForm.
export const GuestAddForm = ({ eventSlug, fieldDefs, existingPersonUuids = [], dialogRef, hideModalDialog, setLoadingModalDialog, onSaved }) => {
  const [pending, setPending] = useState([]) // [{person_uuid, person_name, customData}]
  const [fillValues, setFillValues] = useState({})
  const [error, setError] = useState(null)

  // The multi-picker is fed `pending` (mapped to its own {value,label} shape)
  // as its controlled value, so unchecking an option here and removing a
  // pending row via the "✕" button below both stay in sync automatically.
  const multiValue = pending.map(g => ({ value: g.person_uuid, label: g.person_name }))

  const handleMultiChange = ({ value: nextArray }) => {
    setPending(prev => {
      const prevByUuid = Object.fromEntries(prev.map(g => [g.person_uuid, g]))
      return nextArray.map(o => prevByUuid[o.value] || { person_uuid: o.value, person_name: o.label, customData: {} })
    })
  }

  const removePending = (personUuid) => setPending(prev => prev.filter(g => g.person_uuid !== personUuid))

  const setPendingCustomValue = (personUuid, key, value) =>
    setPending(prev => prev.map(g => g.person_uuid === personUuid ? { ...g, customData: { ...g.customData, [key]: value } } : g))

  const fillAll = (fd) => {
    const value = fd.field_type === 'boolean' ? !!fillValues[fd.field_key] : (fillValues[fd.field_key] ?? '')
    setPending(prev => prev.map(g => ({ ...g, customData: { ...g.customData, [fd.field_key]: value } })))
  }

  const submit = () => {
    setError(null)

    if (!pending.length) {
      setError('Välj minst en person')
      return Promise.resolve()
    }

    setLoadingModalDialog(true)
    return Promise.all(pending.map(g => eventGuestService.create({
      event_slug: eventSlug,
      event_guest_person_uuid: g.person_uuid,
      event_guest_custom_data: g.customData,
    })))
      .then(() => {
        setLoadingModalDialog(false)
        hideModalDialog()
        onSaved && onSaved()
      })
      .catch(err => {
        setLoadingModalDialog(false)
        setError(err.message || 'Kunde inte lägga till gäster')
      })
  }

  if (dialogRef) dialogRef.current = { submit }

  const renderCell = (g, fd) => {
    const value = g.customData[fd.field_key]
    if (fd.field_type === 'boolean') {
      return <Checkbox name={`${g.person_uuid}-${fd.field_key}`} checked={!!value} onChange={() => setPendingCustomValue(g.person_uuid, fd.field_key, !value)} />
    }
    return (
      <input
        className="ev-bulk-input"
        type={fd.field_type === 'number' ? 'number' : 'text'}
        value={value ?? ''}
        onChange={e => setPendingCustomValue(g.person_uuid, fd.field_key, e.target.value)}
      />
    )
  }

  const renderFillControl = (fd) => {
    if (fd.field_type === 'boolean') {
      return (
        <div className="ev-bulk-fill">
          <Checkbox name={`fill-${fd.field_key}`} checked={!!fillValues[fd.field_key]} onChange={() => setFillValues(p => ({ ...p, [fd.field_key]: !p[fd.field_key] }))} label="alla" />
          <button type="button" className="ev-bulk-fill-btn" onClick={() => fillAll(fd)}>Fyll</button>
        </div>
      )
    }
    return (
      <div className="ev-bulk-fill">
        <input
          className="ev-bulk-input"
          type={fd.field_type === 'number' ? 'number' : 'text'}
          placeholder="Fyll alla…"
          value={fillValues[fd.field_key] ?? ''}
          onChange={e => setFillValues(p => ({ ...p, [fd.field_key]: e.target.value }))}
        />
        <button type="button" className="ev-bulk-fill-btn" onClick={() => fillAll(fd)}>Fyll</button>
      </div>
    )
  }

  return (
    <div className="form">
      {error && <div className="form-error">{error}</div>}

      <div className="form-block vertical">
        <PersonMultiPicker
          name="guest_persons"
          label="Välj personer från nätverket"
          value={multiValue}
          onChange={handleMultiChange}
          excludeUuids={existingPersonUuids}
          allowCreate
        />
      </div>

      {pending.length > 0 &&
        <div className="form-block vertical">
          <label>Läggs till ({pending.length})</label>
          <div className="ev-bulk-grid-scroll">
            <table className="ev-bulk-grid">
              <thead>
                <tr>
                  <th className="ev-bulk-namehead">Gäst</th>
                  {fieldDefs.map(fd => (
                    <th key={fd.event_field_def_id}>
                      <div className="ev-bulk-collabel">{fd.field_label}</div>
                      {renderFillControl(fd)}
                    </th>
                  ))}
                  <th className="ev-bulk-removehead"></th>
                </tr>
              </thead>
              <tbody>
                {pending.map(g => (
                  <tr key={g.person_uuid}>
                    <td className="ev-bulk-name">{g.person_name}</td>
                    {fieldDefs.map(fd => <td key={fd.event_field_def_id}>{renderCell(g, fd)}</td>)}
                    <td className="ev-bulk-removecell">
                      <button type="button" className="ev-pending-guest-remove" onClick={() => removePending(g.person_uuid)} title="Ta bort">✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      }
    </div>
  )
}
